import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Zap, Plus, Calendar, Users, TrendingUp, Gift, Megaphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";

interface Promotion {
  id: string;
  name: string;
  description: string;
  awardType: 'multiplier' | 'flat_bonus';
  awardValue: string;
  isActive: boolean;
  validFrom: string | null;
  validUntil: string | null;
  impressions: number;
  conversions: number;
}

interface ContentCreatorOffer {
  id: string;
  title: string;
  offerType: string;
  status: string;
  influencerName: string;
  createdAt: string;
  deadline: string | null;
}

export default function PartnerPromotions() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  
  const { data: promotions, isLoading: promosLoading } = useQuery<Promotion[]>({
    queryKey: ['/api/partner/promotions', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: creatorOffers, isLoading: offersLoading } = useQuery<ContentCreatorOffer[]>({
    queryKey: ['/api/partner/creator-offers', restaurantId],
    enabled: !!restaurantId,
  });
  
  const getPromoLabel = (promo: Promotion) => {
    if (promo.awardType === 'multiplier') {
      return `${parseFloat(promo.awardValue)}x Kudoz`;
    }
    return `+${parseInt(promo.awardValue)} Kudoz Bonus`;
  };
  
  const getOfferStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-promotions-title">Promotions</h1>
            <p className="text-muted-foreground">Create and manage your promotions</p>
          </div>
          
          <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-promo">
                <Plus className="h-4 w-4 mr-1" />
                Create Promotion
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Kudoz Promotion</DialogTitle>
                <DialogDescription>
                  Boost customer engagement with double Kudoz or bonus rewards
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Promotion Name</Label>
                  <Input placeholder="e.g., Weekend Double Kudoz" data-testid="input-promo-name" />
                </div>
                
                <div className="space-y-2">
                  <Label>Promotion Type</Label>
                  <Select>
                    <SelectTrigger data-testid="select-promo-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multiplier">Kudoz Multiplier (e.g., 2x)</SelectItem>
                      <SelectItem value="flat_bonus">Flat Bonus (e.g., +50)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Value</Label>
                  <Input type="number" placeholder="2" min="1" data-testid="input-promo-value" />
                  <p className="text-xs text-muted-foreground">
                    For multiplier: 2 = double Kudoz. For bonus: 50 = +50 extra Kudoz
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" data-testid="input-promo-start" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" data-testid="input-promo-end" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea placeholder="Describe your promotion..." rows={3} data-testid="textarea-promo-desc" />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowCreatePromo(false)}>Cancel</Button>
                  <Button data-testid="button-save-promo">Create Promotion</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        
        <Tabs defaultValue="kudoz-promos">
          <TabsList>
            <TabsTrigger value="kudoz-promos" data-testid="tab-kudoz-promos">
              <Zap className="h-4 w-4 mr-1" />
              Kudoz Promotions
            </TabsTrigger>
            <TabsTrigger value="creator-offers" data-testid="tab-creator-offers">
              <Megaphone className="h-4 w-4 mr-1" />
              Content Creator Offers
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="kudoz-promos" className="mt-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {promosLoading ? (
                [1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-48" />
                ))
              ) : promotions && promotions.length > 0 ? (
                promotions.map((promo) => (
                  <Card key={promo.id} data-testid={`promo-card-${promo.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{promo.name}</CardTitle>
                        <Badge variant={promo.isActive ? "default" : "secondary"}>
                          {promo.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <CardDescription>{promo.description || 'No description'}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-4">
                        <Zap className="h-5 w-5 text-yellow-500" />
                        <span className="text-lg font-bold">{getPromoLabel(promo)}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Impressions</p>
                          <p className="font-medium">{promo.impressions.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Conversions</p>
                          <p className="font-medium">{promo.conversions.toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {promo.validUntil && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-4">
                          <Calendar className="h-3 w-3" />
                          <span>Ends {new Date(promo.validUntil).toLocaleDateString()}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Zap className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No promotions yet</p>
                    <p className="text-sm text-muted-foreground text-center max-w-md">
                      Create a double Kudoz promotion to encourage more orders and reviews from customers
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="creator-offers" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Content Creator Offers</CardTitle>
                  <CardDescription>Collaborate with food content creators for exposure</CardDescription>
                </div>
                <Button variant="outline" data-testid="button-create-offer">
                  <Plus className="h-4 w-4 mr-1" />
                  New Offer
                </Button>
              </CardHeader>
              <CardContent>
                {offersLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : creatorOffers && creatorOffers.length > 0 ? (
                  <div className="space-y-3">
                    {creatorOffers.map((offer) => (
                      <div 
                        key={offer.id} 
                        className="flex items-center justify-between p-4 rounded-lg border"
                        data-testid={`offer-${offer.id}`}
                      >
                        <div>
                          <p className="font-medium">{offer.title}</p>
                          <p className="text-sm text-muted-foreground">
                            To: {offer.influencerName} • {offer.offerType.replace('_', ' ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={getOfferStatusColor(offer.status)}>
                            {offer.status}
                          </Badge>
                          {offer.deadline && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Due: {new Date(offer.deadline).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No creator offers yet</p>
                    <p className="text-sm text-muted-foreground">
                      Browse influencers and send collaboration offers
                    </p>
                    <Button className="mt-4" variant="outline" asChild>
                      <a href="/partner/influencers">Browse Influencers</a>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
}
